import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import dig from 'object-dig';
import moment from 'moment';

import ReceiptIcon from '@material-ui/icons/Receipt';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';
import SyncAltIcon from '@material-ui/icons/SyncAlt';

import Chip from '@material-ui/core/Chip';
import LinkMaterial from '@material-ui/core/Link';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';

import Paper from '~/components/Paper';
import Table from '~/components/Table/Table';
import GridItem from '~/components/GridItem';
import InputDate from '~/components/Input/InputDate';
import GridContainer from '~/components/GridContainer';
import IconButton from '~/components/Button/IconButton';
import InputSelect from '~/components/Input/InputSelect';
import InputSearch from '~/components/Input/InputSearch';
import LinearProgress from '~/components/LinearProgress';
import tableStyles from '~/components/Table/Table/styles';
import InputControl from '~/components/Input/InputControl';
import TablePagination from '~/components/Table/TablePagination';

import { epayTransactionStatusOptions } from '~/config/constants';
import {
  showProduct,
  showEpayTransaction,
} from '~/config/routes';

import toInt from '~/utils/Number/toInt';
import Request from '~/providers/Request';
import { AbilityContext } from '~/contexts/Can';

function PinList() {
  const { t } = useTranslation();
  const ability = useContext(AbilityContext);
  const classesTable = tableStyles();

  const [loading, setLoading] = useState(false);
  const [pins, setPins] = useState([]);
  const [totalPins, setTotalPins] = useState(0);

  const [dataInit, setDataInit] = useState(moment().startOf('month').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('month').format());
  const [product, setProduct] = useState(null);
  const [productsOptions, setProductsOptions] = useState([]);

  const [inputSearch, setInputSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const trAttr = (attr) => t(`epayTransactions.attributes.${attr}`);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  async function fetchProducts() {
    Request.get('/admin/products', null, { normalizeJSON: true })
      .then((response) => {
        setProductsOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setProductsOptions([]);
      });
  }

  async function getEpayTransactions() {
    setLoading(true);

    const filters = {
      start: dataInit,
      end: dataEnd,
      qs: inputSearch,
      page: page + 1,
      per_page: rowsPerPage,
      product_id: dig(product, 'id'),
    };

    Request.get('/admin/epay_transactions', filters, { normalizeJSON: false })
      .then((response) => {
        const { data, meta } = response;
        setLoading(false);
        setPins(data);
        setPage(toInt(meta.page) - 1);
        setTotalPins(toInt(meta.total));
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    getEpayTransactions();
  }, [inputSearch, page, rowsPerPage, product, dataInit, dataEnd]);

  return (
    <>
      <Paper
        Icon={SyncAltIcon}
        iconColor="blue"
        title={t('epayTransactions.defPlural')}
      >
        <GridContainer>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataInit}
              name="date"
              label={t('pins.extras.date_init')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) => {
                setDataInit(moment(date).startOf('day').format());
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataEnd}
              name="date"
              label={t('pins.extras.date_end')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) =>
                setDataEnd(moment(date).endOf('day').format())
              }
            />
          </GridItem>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputControl
              name={'product'}
              value={product}
              label={trAttr('product')}
              inputComponent={InputSelect}
              onChange={({ product }) => {
                setProduct(product);
              }}
              InputProps={{
                isClearable: true,
                labelKey: 'description',
                options: productsOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputSearch
              value={inputSearch}
              label={t('action.search')}
              placeholder={t('epayTransactions.extras.search')}
              onChange={setInputSearch}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <Table
          emptyData={pins.length === 0}
          loading={loading}
          columns={
            <>
              <TableCell className={classesTable.cell}>
                {t('action.actions')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('id')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('tx_id')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('tx_ref')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('status')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('type')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('product')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('created_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('updated_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('result_text')}
              </TableCell>
            </>
          }
        >
          {pins.map((row) => {
            return (
              <TableRow key={row.id} hover className={classesTable.row}>
                <TableCell>
                  {ability.can('show', 'EpayTransactions') && (
                    <Link to={showEpayTransaction.reverse({ id: row.id })}>
                      <IconButton
                        tooltip={t('action.show')}
                        Icon={RemoveRedEyeIcon}
                        iconColor="green"
                      />
                    </Link>
                  )}
                </TableCell>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.tx_id}</TableCell>
                <TableCell>{row.tx_ref}</TableCell>
                <TableCell>{epayTransactionStatusOptions[row.status]}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>
                  {row.product ? (
                    <Chip
                      icon={<ReceiptIcon />}
                      label={dig(row, 'product', 'description')}
                      component={LinkMaterial}
                      href={showProduct.reverse({
                        id: dig(row, 'product', 'id'),
                      })}
                      style={{ cursor: 'pointer' }}
                      target="_blank"
                      color="primary"
                      variant="outlined"
                      onDelete={() => {}}
                      deleteIcon={<OpenInNewIcon />}
                    />
                  ) : null}
                </TableCell>
                <TableCell>
                  {row.created_at
                    ? moment(row.created_at).format('DD/MM/YYYY HH:mm')
                    : null}
                </TableCell>
                <TableCell>
                  {row.updated_at
                    ? moment(row.updated_at).format('DD/MM/YYYY HH:mm')
                    : null}
                </TableCell>
                <TableCell>{row.result_text}</TableCell>
              </TableRow>
            );
          })}
        </Table>

        <LinearProgress loading={loading} />

        <Typography
          variant="subtitle2"
          color="primary"
          align="right"
          style={{ paddingRight: 16, marginTop: 10 }}
        >
          <strong>Total: </strong>
          {totalPins}
        </Typography>

        <TablePagination
          count={totalPins}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
}

export default React.memo(PinList);
